import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import {
  DwButtonCommand,
  DwButtonConfig,
  DwMetaDataConfigContext,
  DwMetadataConfigFiltersComponent,
  DwMetaDataFilterItemConfig,
  DwModalConfig,
  DwModalHandler,
  DwModalResult
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { FilterType } from '../../../../shared/models/filter-type.enum';
import {
  ReportDashboardGlobalFilterDefinition,
  ReportFilterUiConfig
} from '../../models/report-dashboard-definition.model';

export interface ReportDashboardFilterDesignerModalData {
  itemName: string;
  contextNames?: string[];
  filters: DwMetaDataFilterItemConfig<ReportFilterUiConfig>[];
  dataSourceItemNames?: string[];
  globalFilters?: ReportDashboardGlobalFilterDefinition[];
  initialFilterKey?: string;
}

export interface ReportDashboardFilterDesignerModalResult {
  filters: DwMetaDataFilterItemConfig<ReportFilterUiConfig>[];
  globalFilters: ReportDashboardGlobalFilterDefinition[];
}

@Component({
  selector: 'app-report-dashboard-filter-designer-modal',
  templateUrl: './report-dashboard-filter-designer-modal.component.html',
  styleUrls: ['./report-dashboard-filter-designer-modal.component.scss']
})
export class ReportDashboardFilterDesignerModalComponent implements OnInit, DwModalHandler {
  @ViewChild('capFilterDesigner') capFilterDesigner: DwMetadataConfigFiltersComponent;

  modalConfig: DwModalConfig<ReportDashboardFilterDesignerModalData>;
  closeModal = new EventEmitter<DwModalResult<ReportDashboardFilterDesignerModalResult>>();

  configContext: DwMetaDataConfigContext;
  filters: DwMetaDataFilterItemConfig<ReportFilterUiConfig>[] = [];
  globalFilters: ReportDashboardGlobalFilterDefinition[] = [];
  errorMessage: string;

  readonly presentationOptions = [
    { label: 'Automatic from CAP control', value: null },
    { label: 'Dropdown', value: FilterType.Dropdown },
    { label: 'Multi-select', value: FilterType.MultiSelect },
    { label: 'Date', value: FilterType.DatePicker },
    { label: 'Date range', value: FilterType.DateRange },
    { label: 'Reporting period', value: FilterType.ReportDatePeriod },
    { label: 'Yes / No toggle', value: FilterType.Toggle },
    { label: 'Text', value: FilterType.TextInput }
  ];

  ngOnInit(): void {
    this.filters = this.clone(this.modalConfig.data?.filters || []);
    const configuredGlobalFilters = this.clone(this.modalConfig.data?.globalFilters || []);
    this.globalFilters = this.uniqueStrings([
      ...(this.modalConfig.data?.dataSourceItemNames || []),
      ...configuredGlobalFilters.map(filter => filter.dataSourceItemName)
    ]).map(dataSourceItemName => ({
      dataSourceItemName,
      filterExpression: configuredGlobalFilters.find(filter =>
        filter.dataSourceItemName?.toLowerCase() === dataSourceItemName.toLowerCase()
      )?.filterExpression
    }));
    this.configContext = {
      itemName: this.modalConfig.data?.itemName,
      contextNames: this.modalConfig.data?.contextNames || []
    };
  }

  get selectedFilter(): DwMetaDataFilterItemConfig<ReportFilterUiConfig> {
    return this.capFilterDesigner?.selectedFilter as DwMetaDataFilterItemConfig<ReportFilterUiConfig>;
  }

  get selectedUiConfig(): ReportFilterUiConfig {
    if (!this.selectedFilter) {
      return null;
    }
    this.selectedFilter.additionalConfig = this.selectedFilter.additionalConfig || {} as ReportFilterUiConfig;
    return this.selectedFilter.additionalConfig;
  }

  onFiltersChanged(filters: DwMetaDataFilterItemConfig<ReportFilterUiConfig>[]): void {
    this.filters = filters || [];
    this.errorMessage = null;
  }

  onPresentationChanged(presentationType: FilterType): void {
    const ui = this.selectedUiConfig;
    if (!ui) {
      return;
    }
    if (presentationType) {
      ui.presentationType = presentationType;
    } else {
      ui.presentationType = null;
    }
    if (presentationType === FilterType.MultiSelect) {
      ui.allowMultiple = true;
    }
  }

  filterDisplay(filter: DwMetaDataFilterItemConfig<ReportFilterUiConfig>): string {
    return filter?.label || filter?.filterDisplay || filter?.attr || 'Custom filter';
  }

  filterIcon(filter: DwMetaDataFilterItemConfig<ReportFilterUiConfig>): string {
    return filter?.additionalConfig?.iconClass || 'fa fa-filter';
  }

  presentationDisplay(filter: DwMetaDataFilterItemConfig<ReportFilterUiConfig>): string {
    const presentationType = filter?.additionalConfig?.presentationType;
    return this.presentationOptions.find(option => option.value === presentationType)?.label
      || 'Automatic from CAP control';
  }

  get configuredGlobalFilterCount(): number {
    return this.globalFilters.filter(filter => !!filter.filterExpression).length;
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult<ReportDashboardFilterDesignerModalResult>> {
    if (buttonConfig.command !== DwButtonCommand.saveAndClose) {
      return of({
        cancelled: true,
        closeModal: true,
        buttonCommand: buttonConfig.command
      } as DwModalResult<ReportDashboardFilterDesignerModalResult>);
    }

    if (!this.configContext?.itemName) {
      this.errorMessage = 'Set the dashboard default data source before configuring filters.';
      return of({ closeModal: false } as DwModalResult<ReportDashboardFilterDesignerModalResult>);
    }

    this.errorMessage = null;
    return of({
      cancelled: false,
      closeModal: true,
      buttonCommand: buttonConfig.command,
      data: {
        filters: this.clone(this.filters),
        globalFilters: this.clone(this.globalFilters.filter(filter => !!filter.filterExpression))
      }
    } as DwModalResult<ReportDashboardFilterDesignerModalResult>);
  }

  private uniqueStrings(values: string[]): string[] {
    return (values || []).filter((value, index, source) => !!value
      && source.findIndex(item => item?.toLowerCase() === value.toLowerCase()) === index);
  }

  private clone<T>(value: T): T {
    return value === undefined || value === null
      ? value
      : JSON.parse(JSON.stringify(value));
  }
}
